
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ArrowLeft, Loader2, RefreshCw, ArrowUpDown, Settings, Check, FilterX, X, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

// --- CONFIGURATIONS ---
const CLIENT_COLUMNS = [
    { key: 'client', label: 'Client', width: 300 },
    { key: 'articles_p1', label: 'Nr. Articole 2024', width: 120 },
    { key: 'articles_p2', label: 'Nr. Articole 2025', width: 120 },
    { key: 'sales_p1', label: 'Valoare Vanduta 2024', width: 150 },
    { key: 'sales_p2', label: 'Valoare Vanduta 2025', width: 150 },
    { key: 'profit_p1', label: 'Profit 2024', width: 140 },
    { key: 'profit_p2', label: 'Profit 2025', width: 140 },
    { key: 'profit_pct_p1', label: 'Profit % 2024', width: 120 },
    { key: 'profit_pct_p2', label: 'Profit % 2025', width: 120 },
];

const SUPPLIER_COLUMNS = [
    { key: 'supplier', label: 'Furnizor', width: 250 },
    { key: 'client', label: 'Client', width: 250 },
    { key: 'sales_p1', label: 'Vanzari 2024 (P1)', width: 150 },
    { key: 'profit_p1', label: 'Profit 2024 (P1)', width: 140 },
    { key: 'profit_pct_p1', label: 'Marja 2024 (P1) %', width: 120 },
    { key: 'sales_p2', label: 'Vanzari 2025 (P2)', width: 150 },
    { key: 'profit_p2', label: 'Profit 2025 (P2)', width: 140 },
    { key: 'profit_pct_p2', label: 'Marja 2025 (P2) %', width: 120 },
    { key: 'share_p2', label: 'Pondere Client in CA Furn. (P2)', width: 150 },
    { key: 'agent', label: 'Sales Pers.', width: 150 },
    { key: 'sup_clients_count', label: 'Nr. Clienti / Furnizor', width: 150 },
];

const ARTICLE_COLUMNS = [
    { key: 'code', label: 'Cod Articol', width: 150 },
    { key: 'name', label: 'Articol', width: 350 },
    { key: 'qty_p1', label: 'Cantitate 2024', width: 120 },
    { key: 'qty_p2', label: 'Cantitate 2025', width: 120 },
    { key: 'sales_p1', label: 'Valoare Vanduta 2024', width: 150 },
    { key: 'sales_p2', label: 'Valoare Vanduta 2025', width: 150 },
    { key: 'profit_p1', label: 'Profit 2024', width: 140 },
    { key: 'profit_p2', label: 'Profit 2025', width: 140 },
    { key: 'profit_pct_p1', label: 'Profit % 2024', width: 120 },
    { key: 'profit_pct_p2', label: 'Profit % 2025', width: 120 },
];

export default function ClientsAnalysis() {
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(false);
    const [articlesLoading, setArticlesLoading] = useState(false);
    const [table1, setTable1] = useState('');
    const [table2, setTable2] = useState('');
    const [activeTables, setActiveTables] = useState({ t1: null, t2: null });

    // Data States
    const [clientData, setClientData] = useState(null);
    const [articleData, setArticleData] = useState(null);
    const [supplierData, setSupplierData] = useState(null);
    const [detailedData, setDetailedData] = useState(null);

    // Selection State
    const [selectedClient, setSelectedClient] = useState(null);
    const [clientFilterVal, setClientFilterVal] = useState('');
    const filterTimerRef = useRef(null);


    useEffect(() => {
        fetchTables();
    }, []);

    const fetchTables = async () => {
        try {
            const res = await axios.get(`${API}/tables`);
            setTables(res.data || []);
        } catch (e) {
            console.error(e);
            toast.error("Nu am putut incarca lista de fisiere.");
        }
    };

    const handleCompare = async () => {
        if (!table1 || !table2) {
            toast.warning("Te rog selecteaza ambele perioade.");
            return;
        }
        setLoading(true);
        setClientData(null);
        setArticleData(null);
        setDetailedData(null);
        setSelectedClient(null); // Reset selection on new compare

        try {
            setActiveTables({ t1: table1, t2: table2 });
            // Fetch All Clients
            const resClients = await axios.post(`${API}/reports/all-clients-analysis`, { table1, table2 });

            if (resClients.data.error) toast.error(`Clienti: ${resClients.data.error}`);
            else setClientData(resClients.data);

            // Fetch Suppliers
            const resSuppliers = await axios.post(`${API}/reports/suppliers-clients-analysis`, { table1, table2 });
            if (resSuppliers.data.error) toast.error(`Furnizori: ${resSuppliers.data.error}`);
            else setSupplierData(resSuppliers.data);

            // Fetch Detailed Comparison
            const resDetailed = await axios.post(`${API}/reports/detailed-clients-comparison`, { table1, table2 });
            if (resDetailed.data.error) toast.error(`Detalii: ${resDetailed.data.error}`);
            else setDetailedData(resDetailed.data);

            // Fetch All Articles initially (No Filter)
            await fetchArticles(null);

            toast.success("Date incarcate cu succes!");

        } catch (e) {
            console.error(e);
            toast.error("Eroare la incarcarea datelor.");
        } finally {
            setLoading(false);
        }
    };

    const fetchArticles = async (clientFilter = null) => {
        if (!table1 || !table2) return;
        setArticlesLoading(true);
        try {
            const res = await axios.post(`${API}/reports/all-clients-articles-analysis`, {
                table1,
                table2,
                client_filter: clientFilter
            });

            if (res.data.error) {
                toast.error(`Articole: ${res.data.error}`);
                setArticleData([]);
            } else {
                setArticleData(res.data);
            }
        } catch (e) {
            console.error(e);
            toast.error("Eroare la incarcarea articolelor.");
        } finally {
            setArticlesLoading(false);
        }
    };

    const handleClientClick = (row) => {
        const clientName = row.client;
        if (selectedClient === clientName) {
            // Deselect if same clicked
            handleClearSelection();
        } else {
            setSelectedClient(clientName);
            fetchArticles(clientName);
            toast.info(`Filtrare articole pentru: ${clientName}`);
        }
    };

    const handleClearSelection = () => {
        setSelectedClient(null);
        fetchArticles(null);
    };

    const p1Label = getYearLabel(table1) || 'P1';
    const p2Label = getYearLabel(table2) || 'P2';

    return (
        <div className="p-8 space-y-8 pb-40">
            <div className="flex items-center space-x-4">
                <Link to="/reports">
                    <Button variant="ghost" className="text-gray-400 hover:text-white">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Inapoi la Rapoarte
                    </Button>
                </Link>
                <div className="flex-1">
                    <h1 className="text-3xl font-bold text-white">Analiza Toti Clientii</h1>
                </div>
            </div>

            {/* Selection Card */}
            <Card className="bg-[#18181b] border-white/10">
                <CardHeader>
                    <CardTitle className="text-gray-300 text-lg">Selectie Perioade</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                    <div className="space-y-2">
                        <label className="text-sm text-gray-400">Perioada 1 (Ex: 2024)</label>
                        <Select value={table1} onValueChange={setTable1}>
                            <SelectTrigger className="bg-[#27272a] border-white/20 text-white"><SelectValue placeholder="Selecteaza fisier..." /></SelectTrigger>
                            <SelectContent className="bg-[#27272a] border-white/20 text-white h-[300px]">
                                {tables.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm text-gray-400">Perioada 2 (Ex: 2025)</label>
                        <Select value={table2} onValueChange={setTable2}>
                            <SelectTrigger className="bg-[#27272a] border-white/20 text-white"><SelectValue placeholder="Selecteaza fisier..." /></SelectTrigger>
                            <SelectContent className="bg-[#27272a] border-white/20 text-white h-[300px]">
                                {tables.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button onClick={handleCompare} disabled={loading || !table1 || !table2} className="bg-red-600 hover:bg-red-700 text-white">
                        {loading ? <Loader2 className="animate-spin mr-2" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                        Genereaza Raport
                    </Button>
                </CardContent>
            </Card>

            {/* TABEL CLIENTI */}
            {clientData && (
                <ExtendedTable
                    title="Raport Clienti (Global)"
                    data={clientData}
                    columnsConfig={CLIENT_COLUMNS}
                    p1Label={p1Label}
                    p2Label={p2Label}
                    onRowClick={handleClientClick}
                    selectedRowKey="client"
                    selectedRowValue={selectedClient}
                    extraHeaderContent={
                        selectedClient && (
                            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleClearSelection(); }} className="ml-4 border-yellow-500 text-yellow-500 hover:bg-yellow-500/10">
                                <X className="mr-2 h-3 w-3" />
                                Clear Filter: {selectedClient}
                            </Button>
                        )
                    }

                    onFilterChange={(filters) => {
                        const val = filters['client'] || '';
                        if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
                        filterTimerRef.current = setTimeout(() => {
                            if (val !== clientFilterVal) {
                                setClientFilterVal(val);
                                // If a row is selected, we might want to prioritize that, or clear it?
                                // User intent: "If I filter...". Usually filtering overrides selection or clears it.
                                if (!selectedClient) {
                                    fetchArticles(val || null);
                                    if (val) toast.info(`Filtrare articole: ${val}`);
                                }
                            }
                        }, 800);
                    }}
                />
            )}



            {/* TABEL ARTICOLE */}
            {articleData && (
                <ExtendedTable
                    title={
                        selectedClient ? `Raport Articole - ${selectedClient}` :
                            clientFilterVal ? `Raport Articole (Filtrat: ${clientFilterVal})` :
                                "Raport Articole (Toate Vanzarile Global)"
                    }
                    data={articleData}
                    columnsConfig={ARTICLE_COLUMNS}
                    p1Label={p1Label}
                    p2Label={p2Label}
                    isLoading={articlesLoading}
                />
            )}

            {/* TABEL FURNIZORI - CLIENTI */}
            {supplierData && (
                <ExtendedTable
                    title="Raport Furnizori - Clienti"
                    data={supplierData}
                    columnsConfig={SUPPLIER_COLUMNS}
                    p1Label={p1Label}
                    p2Label={p2Label}
                // No interactive selection requested for this table yet
                />
            )}

            {/* TABEL DETALIAT DE COMPARATIE */}
            {detailedData && (
                <>
                    <DetailedClientsTable
                        title="Analiza Detaliata Clienti (P1 vs P2)"
                        data={detailedData}
                        p1Label={p1Label}
                        p2Label={p2Label}
                    />

                    <TopClientsTableNew
                        title="Top 15 Clienti"
                        data={detailedData}
                        p1Label={p1Label}
                        p2Label={p2Label}
                        table1={activeTables?.t1}
                        table2={activeTables?.t2}
                    />
                </>
            )}
        </div>
    );
}

// --- REUSABLE EXTENDED TABLE COMPONENT ---
function ExtendedTable({ title, data, columnsConfig, p1Label, p2Label, headerClassName, onRowClick, selectedRowKey, selectedRowValue, extraHeaderContent, isLoading, onFilterChange }) {
    const [sortConfig, setSortConfig] = useState({ key: columnsConfig[1]?.key || 'value', direction: 'desc' });
    const [columnFilters, setColumnFilters] = useState({});
    const [columnWidths, setColumnWidths] = useState(
        columnsConfig.reduce((acc, col) => ({ ...acc, [col.key]: col.width }), {})
    );
    const [visibleColumns, setVisibleColumns] = useState(
        columnsConfig.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
    );

    // --- PAGINATION STATE ---
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    // Reset page when filters/sort change
    useEffect(() => {
        setCurrentPage(1);
        if (onFilterChange) onFilterChange(columnFilters);
    }, [columnFilters, sortConfig, data]); // removed onFilterChange from deps to avoid loop if parent recreates it (though handle with ref if needed, usually fine)

    // Filter & Sort Logic
    const formatMoney = (val) => {
        if (val === undefined || val === null) return '-';
        return Number(val).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatPercent = (val) => {
        if (val === undefined || val === null) return '-';
        return `${(val * 100).toFixed(0)}%`;
    };

    const formatQty = (val) => {
        if (val === undefined || val === null) return '-';
        return Number(val).toLocaleString('ro-RO', { maximumFractionDigits: 0 });
    }

    const filteredData = useMemo(() => {
        if (!data) return [];
        let items = [...data];

        // 1. Column Filters
        const activeFilterKeys = Object.keys(columnFilters).filter(k => columnFilters[k]);
        if (activeFilterKeys.length > 0) {
            items = items.filter(item => {
                return activeFilterKeys.every(key => {
                    const filterVal = columnFilters[key].toLowerCase();
                    let rawVal = item[key];
                    let strVal = '';

                    if (key.includes('qty')) {
                        strVal = String(Math.round(rawVal || 0));
                    } else if (typeof rawVal === 'number') {
                        if (key.includes('pct')) {
                            strVal = `${(rawVal * 100).toFixed(0)}%`; // Match displayed percent
                        } else {
                            strVal = rawVal.toFixed(2); // Match displayed money approximately
                        }
                    } else {
                        strVal = String(rawVal || '');
                    }

                    return strVal.toLowerCase().includes(filterVal);
                });
            });
        }

        // 2. Sort
        if (sortConfig.key) {
            items.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                if (typeof bVal === 'string') bVal = bVal.toLowerCase();

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return items;
    }, [data, columnFilters, sortConfig]);

    // Derived Logic for Pagination
    const totalPages = Math.ceil(filteredData.length / pageSize);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredData.slice(start, start + pageSize);
    }, [filteredData, currentPage]);

    const handleNext = () => setCurrentPage(p => Math.min(totalPages, p + 1));
    const handlePrev = () => setCurrentPage(p => Math.max(1, p - 1));

    const handleSort = (key) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handleResizeStart = (e, key) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startWidth = columnWidths[key];
        const handleMouseMove = (ev) => {
            const diff = ev.clientX - startX;
            setColumnWidths(prev => ({ ...prev, [key]: Math.max(50, startWidth + diff) }));
        };
        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'default';
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'col-resize';
    };

    const toggleColumn = (key) => setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
    const clearFilters = () => setColumnFilters({});

    // --- EXPORT LOGIC ---
    const handleExport = async () => {
        try {
            const XLSX = await import('xlsx');

            // Prepare Data
            const exportData = filteredData.map(row => {
                const newRow = {};
                columnsConfig.forEach(col => {
                    if (visibleColumns[col.key]) {
                        // Use raw values or formatted? Raw is better for Excel math
                        // But headers should be labels
                        let val = row[col.key];
                        // Basic Cleanup
                        if (val === null || val === undefined) val = "";

                        // Human Readable Header
                        const header = col.label.replace('2024', p1Label).replace('2025', p2Label);
                        newRow[header] = val;
                    }
                });
                return newRow;
            });

            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Raport");
            XLSX.writeFile(wb, `${title.replace(/[^a-z0-9]/gi, '_')}.xlsx`);
            toast.success("Export realizat!");
        } catch (e) {
            console.error(e);
            toast.error("Eroare export.");
        }
    };

    // --- TOTALS LOGIC ---
    const totals = useMemo(() => {
        if (!filteredData.length) return null;
        const sums = {};
        columnsConfig.forEach(col => {
            const k = col.key;
            // Check if column is numeric-ish (sales, profit, qty, count)
            if (k.includes('sales') || k.includes('profit') || k.includes('qty') || k.includes('count') || k.includes('articles')) {
                // Skip percents for simple sum, handle separately if needed (weighted avg?)
                // For now, simple sum for absolute values
                if (k.includes('pct') || k.includes('share')) {
                    sums[k] = 0; // Percents don't sum nicely usually
                } else {
                    sums[k] = filteredData.reduce((acc, curr) => acc + (Number(curr[k]) || 0), 0);
                }
            }
        });
        return sums;
    }, [filteredData, columnsConfig]);

    return (
        <Card className="bg-[#18181b] border-white/10 relative">
            {isLoading && (
                <div className="absolute inset-0 bg-black/50 z-50 flex items-center justify-center rounded-lg backdrop-blur-sm">
                    <Loader2 className="animate-spin text-white h-8 w-8" />
                </div>
            )}
            <div className="p-4 border-b border-white/10 flex justify-between items-center bg-yellow-400 rounded-t-lg">
                <div className="flex items-center gap-4">
                    <h3 className="text-lg font-bold text-black mr-4">{title}</h3>
                    {extraHeaderContent}
                    <Button onClick={handleExport} size="sm" className="bg-black text-yellow-400 hover:bg-black/80 border-none h-7">
                        <Download className="mr-2 h-3 w-3" /> Export
                    </Button>
                    <span className="text-black/80 text-sm border-l border-black/20 pl-4 ml-2 font-medium">
                        {filteredData.length} rezultate • Pagina {currentPage} din {Math.max(1, totalPages)}
                    </span>
                    {Object.keys(columnFilters).some(k => columnFilters[k]) && (
                        <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-600 hover:text-red-700 hover:bg-red-100 h-8">
                            <FilterX className="mr-2 h-3 w-3" /> Reset Filtre
                        </Button>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePrev}
                        disabled={currentPage === 1}
                        className="border-black/20 text-black hover:bg-black/10 disabled:opacity-50"
                    >
                        Anterior
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNext}
                        disabled={currentPage >= totalPages}
                        className="border-black/20 text-black hover:bg-black/10 disabled:opacity-50"
                    >
                        Urmator
                    </Button>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="border-black/20 text-black hover:bg-black/10">
                                <Settings className="mr-2 h-4 w-4" /> Coloane
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 bg-[#18181b] border-white/20 text-white p-0">
                            <div className="p-3 border-b border-white/10 bg-white/5"><h4 className="font-medium text-sm text-gray-400">Selecteaza Coloane</h4></div>
                            <div className="p-1 max-h-[300px] overflow-y-auto">
                                {columnsConfig.map(col => (
                                    <div key={col.key} onClick={() => toggleColumn(col.key)} className="flex items-center space-x-2 p-2 hover:bg-white/10 rounded cursor-pointer transition-colors">
                                        <div className={`shrink-0 w-4 h-4 border border-white/40 rounded flex items-center justify-center ${visibleColumns[col.key] ? 'bg-red-600 border-red-600' : ''}`}>
                                            {visibleColumns[col.key] && <Check className="h-3 w-3 text-white" />}
                                        </div>
                                        <span className="text-sm select-none">{col.label}</span>
                                    </div>
                                ))}
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>

            <div className="overflow-x-auto">
                <Table style={{ tableLayout: 'fixed', minWidth: '100%' }}>
                    <TableHeader>
                        <TableRow className={`border-white/10 ${headerClassName || 'bg-[#27272a] hover:bg-[#27272a]'}`}>
                            {columnsConfig.map(col => visibleColumns[col.key] && (
                                <TableHead
                                    key={col.key}
                                    style={{ width: columnWidths[col.key], position: 'relative' }}
                                    className={`font-bold border-r border-white/10 select-none ${headerClassName ? 'text-black font-extrabold !bg-yellow-400' : 'text-gray-200'}`}
                                >
                                    <div className="flex items-center justify-between cursor-pointer py-2" onClick={() => handleSort(col.key)}>
                                        <span className="truncate" title={col.label}>
                                            {col.label.replace('2024', p1Label).replace('2025', p2Label)}
                                        </span>
                                        {sortConfig.key === col.key && <ArrowUpDown className="h-4 w-4 ml-1 opacity-50 shrink-0" />}
                                    </div>
                                    <div className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-black/20 transition-colors z-10" onMouseDown={(e) => handleResizeStart(e, col.key)} />
                                </TableHead>
                            ))}
                        </TableRow>
                        <TableRow className={`border-white/10 ${headerClassName || 'bg-[#27272a] hover:bg-[#27272a]'}`}>
                            {columnsConfig.map(col => visibleColumns[col.key] && (
                                <TableHead key={`filter-${col.key}`} className={`p-2 border-r border-white/10 ${headerClassName ? '!bg-yellow-400' : ''}`}>
                                    <Input
                                        value={columnFilters[col.key] || ''}
                                        onChange={(e) => setColumnFilters(p => ({ ...p, [col.key]: e.target.value }))}
                                        placeholder="Filtru..."
                                        className={`h-8 text-xs border-white/20 focus:ring-red-500 ${headerClassName ? 'bg-white/80 text-black placeholder:text-gray-500' : 'bg-[#18181b] text-white'}`}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginatedData.map((row, idx) => {
                            const isSelected = selectedRowKey && row[selectedRowKey] === selectedRowValue;
                            return (
                                <TableRow
                                    key={idx}
                                    className={`border-white/10 cursor-pointer transition-colors ${isSelected ? 'bg-yellow-500/20 hover:bg-yellow-500/30' : 'hover:bg-white/5'}`}
                                    onClick={() => onRowClick && onRowClick(row)}
                                >
                                    {columnsConfig.map(col => visibleColumns[col.key] && (
                                        <TableCell key={col.key} className="border-r border-white/10 truncate font-mono text-white">
                                            {(() => {
                                                const val = row[col.key];
                                                if (['client', 'name', 'code', 'supplier', 'agent'].includes(col.key)) return <span className="font-sans font-bold">{val}</span>;
                                                if (col.key.includes('qty') || col.key.includes('count')) return <span className="text-right block text-gray-300">{formatQty(val)}</span>;
                                                if ((col.key.includes('sales') || col.key.includes('profit')) && !col.key.includes('pct')) return <span className="text-right block">{formatMoney(val)}</span>;

                                                if (col.key.includes('pct') || col.key.includes('share')) {
                                                    const percent = Math.min(100, Math.max(0, (val || 0) * 100));
                                                    return (
                                                        <div className="relative w-full h-7 bg-black rounded-sm overflow-hidden border border-white/10">
                                                            <div
                                                                className="absolute top-0 left-0 h-full bg-yellow-400 transition-all duration-500 ease-out"
                                                                style={{ width: `${percent}%` }}
                                                            />
                                                            <span className="absolute inset-0 flex items-center justify-end px-2 text-xs font-bold text-white mix-blend-difference z-10">
                                                                {formatPercent(val)}
                                                            </span>
                                                        </div>
                                                    );
                                                }
                                                return val;
                                            })()}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                        {filteredData.length === 0 && (
                            <TableRow><TableCell colSpan={columnsConfig.length} className="text-center text-gray-500 py-8">Nu sunt date.</TableCell></TableRow>
                        )}
                    </TableBody>
                    {/* TOTALS FOOTER */}
                    {filteredData.length > 0 && (
                        <tfoot className="bg-[#27272a] border-t border-white/20 font-bold text-white sticky bottom-0 z-20">
                            <TableRow className="hover:bg-[#27272a]">
                                {columnsConfig.map(col => visibleColumns[col.key] && (
                                    <TableCell key={col.key} className="border-r border-white/10 text-right px-2 py-3 text-xs">
                                        {(() => {
                                            if (['client', 'supplier', 'name'].includes(col.key)) return <span className="text-yellow-400 uppercase">Total</span>;
                                            // If we have a sum
                                            const val = totals?.[col.key];
                                            if (val !== undefined && val !== null) {
                                                if (col.key.includes('qty') || col.key.includes('count') || col.key.includes('articles')) return formatQty(val);
                                                if (col.key.includes('pct') || col.key.includes('share')) return "-";
                                                return formatMoney(val);
                                            }
                                            return "";
                                        })()}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </tfoot>
                    )}

                </Table>
            </div>
        </Card >
    );
}

// Helpers
const getYearLabel = (tableName) => {
    if (!tableName) return '';
    const parts = tableName.split(/[_/]/);
    const year = parts.find(p => /^\d{4}$/.test(p));
    return year || tableName;
};

// --- NEW DETAILED CLIENTS TABLE COMPONENT ---
function DetailedClientsTable({ title, data, p1Label, p2Label }) {
    const [sortConfig, setSortConfig] = useState({ key: 'sales_2025', direction: 'desc' });
    const [filters, setFilters] = useState({});

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 15;

    // Reset page on filter/sort
    useEffect(() => setCurrentPage(1), [filters, sortConfig, data]);

    // Helpers
    const formatMoney = (val) => val ? Number(val).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
    const formatInt = (val) => val ? Number(val).toLocaleString('ro-RO') : '0';
    const formatPct = (val) => val ? `${(val * 100).toFixed(0)}%` : '0%';

    const filteredData = useMemo(() => {
        if (!data) return [];
        let items = [...data];

        // Filter
        const activeKeys = Object.keys(filters).filter(k => filters[k]);
        if (activeKeys.length > 0) {
            items = items.filter(item => {
                return activeKeys.every(key => {
                    const fVal = filters[key].toLowerCase();
                    const val = item[key];
                    // Special handling for growth/diff if needed, else string match
                    return String(val || '').toLowerCase().includes(fVal);
                });
            });
        }

        // Sort
        if (sortConfig.key) {
            items.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                // Strings case insensitive
                if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                if (typeof bVal === 'string') bVal = bVal.toLowerCase();

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return items;

    }, [data, filters, sortConfig]);

    const totalPages = Math.ceil(filteredData.length / pageSize);
    const paginated = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const handleSort = (key) => {
        setSortConfig(curr => ({ key, direction: curr.key === key && curr.direction === 'asc' ? 'desc' : 'asc' }));
    };

    const renderHeaderCell = (label, sortKey, width, rowSpan = 1, colSpan = 1) => (
        <TableHead
            colSpan={colSpan}
            rowSpan={rowSpan}
            style={{ width }}
            className="bg-yellow-400 text-black font-extrabold border border-black/20 text-center p-1 select-none cursor-pointer hover:bg-yellow-500/80 transition-colors h-auto"
            onClick={() => sortKey && handleSort(sortKey)}
        >
            <div className="flex items-center justify-center gap-1">
                {label}
                {sortKey && sortConfig.key === sortKey && <ArrowUpDown className="h-3 w-3" />}
            </div>
        </TableHead>
    );

    const renderFilterCell = (key) => (
        <TableHead className="bg-yellow-400 p-1 border border-black/20">
            <Input
                placeholder="..."
                className="h-7 text-xs bg-white text-black border-none focus:ring-1 focus:ring-black px-1"
                value={filters[key] || ''}
                onChange={e => setFilters(prev => ({ ...prev, [key]: e.target.value }))}
            />
        </TableHead>
    );



    // --- EXPORT ---
    const handleExport = async () => {
        try {
            const XLSX = await import('xlsx');
            const ws = XLSX.utils.json_to_sheet(filteredData.map(row => ({
                "Client": row.client,
                "Status vs P1": row.status_vs_2024,
                "Status P2": row.status_2025,
                "Vanzari 2024": row.sales_2024,
                "Vanzari 2025": row.sales_2025,
                "Vanzari Diff": row.sales_diff,
                "Vanzari Crestere %": formatPct(row.sales_growth),
                "Profit 2024": row.profit_2024,
                "Profit 2025": row.profit_2025,
                "Profit Diff": row.profit_diff,
                "Profit Crestere %": formatPct(row.profit_growth),
                "Pondere 2024": formatPct(row.share_2024),
                "Pondere 2025": formatPct(row.share_2025),
                "Furnizori 2024": row.suppliers_2024,
                "Furnizori 2025": row.suppliers_2025,
                "Articole 2024": row.articles_2024,
                "Articole 2025": row.articles_2025,
                "Agent": row.agent
            })));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Detaliat");
            XLSX.writeFile(wb, "Analiza_Detaliata.xlsx");
            toast.success("Export realizat!");
        } catch (e) {
            console.error(e);
            toast.error("Eroare export.");
        }
    };

    // --- TOTALS ---
    const totals = useMemo(() => {
        if (!filteredData.length) return null;
        const t = {
            sales_2024: 0, sales_2025: 0,
            profit_2024: 0, profit_2025: 0,
            suppliers_2024: 0, suppliers_2025: 0,
            articles_2024: 0, articles_2025: 0,
            share_2024: 0, share_2025: 0
        };
        filteredData.forEach(row => {
            t.sales_2024 += Number(row.sales_2024) || 0;
            t.sales_2025 += Number(row.sales_2025) || 0;
            t.profit_2024 += Number(row.profit_2024) || 0;
            t.profit_2025 += Number(row.profit_2025) || 0;
            t.suppliers_2024 += Number(row.suppliers_2024) || 0;
            t.suppliers_2025 += Number(row.suppliers_2025) || 0;
            t.articles_2024 += Number(row.articles_2024) || 0;
            t.articles_2025 += Number(row.articles_2025) || 0;
            t.share_2024 += Number(row.share_2024) || 0;
            t.share_2025 += Number(row.share_2025) || 0;
        });

        // Derived
        t.sales_diff = t.sales_2025 - t.sales_2024;
        t.profit_diff = t.profit_2025 - t.profit_2024;
        t.sales_growth = t.sales_2024 ? (t.sales_diff / t.sales_2024) : 0;
        t.profit_growth = t.profit_2024 ? (t.profit_diff / t.profit_2024) : 0;

        return t;
    }, [filteredData]);

    return (
        <Card className="bg-[#18181b] border-white/10 mt-8">
            <div className="p-4 border-b border-white/10 flex justify-between items-center bg-yellow-400 rounded-t-lg">
                <h3 className="text-lg font-bold text-black">{title}</h3>
                <div className="flex items-center gap-4">
                    <Button onClick={handleExport} size="sm" className="bg-black text-yellow-400 hover:bg-black/80 border-none h-7">
                        <Download className="mr-2 h-3 w-3" /> Export
                    </Button>
                    <span className="text-sm font-bold text-black border-l border-black/20 pl-4">{filteredData.length} intrari</span>
                </div>
            </div>

            <div className="overflow-x-auto">
                <Table className="border-collapse w-full text-xs">
                    <TableHeader>
                        {/* ROW 1: GROUPS */}
                        <TableRow className="border-none hover:bg-transparent">
                            {renderHeaderCell("Client", "client", 250, 2)}
                            {renderHeaderCell("Clienti noi in 2025 fata de 2024", null, 0, 1, 2)}
                            {renderHeaderCell("Vanzari", null, 0, 1, 4)}
                            {renderHeaderCell("Profit", null, 0, 1, 4)}
                            {renderHeaderCell("Pondere clienti CA", null, 0, 1, 2)}
                            {renderHeaderCell("nr furnizori / client", null, 0, 1, 2)}
                            {renderHeaderCell("nr articole / client", null, 0, 1, 2)}
                            {renderHeaderCell("Sales Pers.", "agent", 150, 2)}
                        </TableRow>

                        {/* ROW 2: SUB-COLUMNS */}
                        <TableRow className="border-none hover:bg-transparent">
                            {/* Clienti Noi Group */}
                            {renderHeaderCell("Status vs " + p1Label, "status_vs_2024", 120)}
                            {renderHeaderCell("status " + p2Label, "status_2025", 120)}

                            {/* Vanzari Group */}
                            {renderHeaderCell("Vanzari " + p1Label, "sales_2024", 100)}
                            {renderHeaderCell("Vanzari " + p2Label, "sales_2025", 100)}
                            {renderHeaderCell("diferenta", "sales_diff", 100)}
                            {renderHeaderCell("crestere %", "sales_growth", 100)}

                            {/* Profit Group */}
                            {renderHeaderCell("Profit " + p1Label, "profit_2024", 100)}
                            {renderHeaderCell("Profit " + p2Label, "profit_2025", 100)}
                            {renderHeaderCell("diferenta", "profit_diff", 100)}
                            {renderHeaderCell("crestere %", "profit_growth", 100)}

                            {/* Pondere Group */}
                            {renderHeaderCell("% din total " + p1Label, "share_2024", 80)}
                            {renderHeaderCell("% din total " + p2Label, "share_2025", 80)}

                            {/* Furnizori Group */}
                            {renderHeaderCell(p1Label, "suppliers_2024", 60)}
                            {renderHeaderCell(p2Label, "suppliers_2025", 60)}

                            {/* Articole Group */}
                            {renderHeaderCell(p1Label, "articles_2024", 60)}
                            {renderHeaderCell(p2Label, "articles_2025", 60)}
                        </TableRow>

                        {/* ROW 3: FILTERS */}
                        <TableRow>
                            {renderFilterCell("client")}
                            {renderFilterCell("status_vs_2024")}
                            {renderFilterCell("status_2025")}

                            {renderFilterCell("sales_2024")}
                            {renderFilterCell("sales_2025")}
                            {renderFilterCell("sales_diff")}
                            {renderFilterCell("sales_growth")}

                            {renderFilterCell("profit_2024")}
                            {renderFilterCell("profit_2025")}
                            {renderFilterCell("profit_diff")}
                            {renderFilterCell("profit_growth")}

                            {renderFilterCell("share_2024")}
                            {renderFilterCell("share_2025")}

                            {renderFilterCell("suppliers_2024")}
                            {renderFilterCell("suppliers_2025")}

                            {renderFilterCell("articles_2024")}
                            {renderFilterCell("articles_2025")}

                            {renderFilterCell("agent")}
                        </TableRow>
                    </TableHeader>

                    <TableBody>
                        {paginated.map((row, i) => {
                            // Helper for Growth Bar
                            const renderGrowth = (val, diff) => {
                                const isPos = diff >= 0;
                                const color = isPos ? 'bg-green-500' : 'bg-red-500';
                                const widthPct = Math.min(100, Math.abs(val * 100)); // Cap at 100 for bar width
                                return (
                                    <div className="relative w-full h-6 bg-gray-800 rounded overflow-hidden">
                                        <div className={`absolute top-0 left-0 h-full ${color}`} style={{ width: `${widthPct}%` }} />
                                        <span className="absolute inset-0 flex items-center justify-end px-1 text-[10px] font-bold text-white mix-blend-difference">
                                            {formatPct(val)}
                                        </span>
                                    </div>
                                )
                            };

                            // Helper for Status
                            const statusColor = (s) => {
                                if (s === 'Client pastrat') return 'bg-green-100 text-green-800';
                                if (s === 'Client nou') return 'bg-blue-100 text-blue-800';
                                if (s === 'Client pierdut') return 'bg-red-100 text-red-800';
                                if (s === 'Client transferat') return 'bg-orange-100 text-orange-800';
                                return 'text-gray-300';
                            };

                            return (
                                <TableRow key={i} className="hover:bg-white/5 border-b border-white/5 text-gray-300">
                                    <TableCell className="font-bold text-white border-r border-white/10">{row.client}</TableCell>

                                    <TableCell className="border-r border-white/10 text-center">{row.status_vs_2024}</TableCell>
                                    <TableCell className="border-r border-white/10 text-center">
                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${statusColor(row.status_2025)}`}>
                                            {row.status_2025}
                                        </span>
                                    </TableCell>

                                    <TableCell className="text-right border-r border-white/10">{formatMoney(row.sales_2024)}</TableCell>
                                    <TableCell className="text-right border-r border-white/10 font-bold text-white">{formatMoney(row.sales_2025)}</TableCell>
                                    <TableCell className="text-right border-r border-white/10">{formatMoney(row.sales_diff)}</TableCell>
                                    <TableCell className="border-r border-white/10 p-1">{renderGrowth(row.sales_growth, row.sales_diff)}</TableCell>

                                    <TableCell className="text-right border-r border-white/10">{formatMoney(row.profit_2024)}</TableCell>
                                    <TableCell className="text-right border-r border-white/10 font-bold text-white">{formatMoney(row.profit_2025)}</TableCell>
                                    <TableCell className="text-right border-r border-white/10">{formatMoney(row.profit_diff)}</TableCell>
                                    <TableCell className="border-r border-white/10 p-1">{renderGrowth(row.profit_growth, row.profit_diff)}</TableCell>

                                    <TableCell className="text-right border-r border-white/10">{formatPct(row.share_2024)}</TableCell>
                                    <TableCell className="text-right border-r border-white/10">{formatPct(row.share_2025)}</TableCell>

                                    <TableCell className="text-center border-r border-white/10">{formatInt(row.suppliers_2024)}</TableCell>
                                    <TableCell className="text-center border-r border-white/10 font-bold text-white">{formatInt(row.suppliers_2025)}</TableCell>

                                    <TableCell className="text-center border-r border-white/10">{formatInt(row.articles_2024)}</TableCell>
                                    <TableCell className="text-center border-r border-white/10 font-bold text-white">{formatInt(row.articles_2025)}</TableCell>

                                    <TableCell className="italic text-gray-400 text-xs">{row.agent}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                    {/* TOTALS FOOTER */}
                    {totals && (
                        <tfoot className="bg-[#27272a] border-t border-white/20 font-bold text-white sticky bottom-0 z-20">
                            <TableRow className="hover:bg-[#27272a]">
                                <TableCell className="border-r border-white/10 text-yellow-400 uppercase font-bold pl-2" colSpan={3}>Total</TableCell>

                                <TableCell className="text-right border-r border-white/10">{formatMoney(totals.sales_2024)}</TableCell>
                                <TableCell className="text-right border-r border-white/10 text-yellow-400">{formatMoney(totals.sales_2025)}</TableCell>
                                <TableCell className="text-right border-r border-white/10">{formatMoney(totals.sales_diff)}</TableCell>
                                <TableCell className="border-r border-white/10 text-center">{formatPct(totals.sales_growth)}</TableCell>

                                <TableCell className="text-right border-r border-white/10">{formatMoney(totals.profit_2024)}</TableCell>
                                <TableCell className="text-right border-r border-white/10 text-yellow-400">{formatMoney(totals.profit_2025)}</TableCell>
                                <TableCell className="text-right border-r border-white/10">{formatMoney(totals.profit_diff)}</TableCell>
                                <TableCell className="border-r border-white/10 text-center">{formatPct(totals.profit_growth)}</TableCell>

                                <TableCell className="text-right border-r border-white/10">{formatPct(totals.share_2024)}</TableCell>
                                <TableCell className="text-right border-r border-white/10">{formatPct(totals.share_2025)}</TableCell>

                                <TableCell className="text-center border-r border-white/10">{formatInt(totals.suppliers_2024)}</TableCell>
                                <TableCell className="text-center border-r border-white/10">{formatInt(totals.suppliers_2025)}</TableCell>

                                <TableCell className="text-center border-r border-white/10">{formatInt(totals.articles_2024)}</TableCell>
                                <TableCell className="text-center border-r border-white/10">{formatInt(totals.articles_2025)}</TableCell>

                                <TableCell className="border-r border-white/10"></TableCell>
                            </TableRow>
                        </tfoot>
                    )}
                </Table>
            </div>
            {/* Simple Pagination */}
            <div className="flex justify-between items-center p-2 bg-yellow-400 rounded-b-lg">
                <Button size="sm" variant="outline" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="border-black/20 text-black">Anterior</Button>
                <span className="text-black font-bold text-xs">Pagina {currentPage} din {totalPages}</span>
                <Button size="sm" variant="outline" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="border-black/20 text-black">Urmator</Button>
            </div>
        </Card>
    );
}



// --- TOP 15 CLIENTS TABLE COMPONENT (NEW) ---
function TopClientsTableNew({ title, data, p1Label, p2Label, table1, table2 }) {
    // --- Sorting State ---
    const [sortConfig, setSortConfig] = useState({ key: 'sales_2025', direction: 'desc' });
    const handleSort = (key) => {
        let direction = 'desc';
        if (sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc';
        }
        setSortConfig({ key, direction });
    };

    // --- Filter State ---
    const [filters, setFilters] = useState({});
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // --- Export Handler ---
    const handleExport = async () => {
        if (!table1 || !table2) {
            toast.error("Setarile tabelelor nu sunt active.");
            return;
        }
        try {
            const response = await axios.post(`${API}/reports/export-top-clients`, {
                table1, table2,
                client_filter: null
            }, { responseType: 'blob' });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Top15_Clienti.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error(err);
            toast.error("Eroare la export.");
        }
    };

    // --- Processing Data (Filter -> Sort -> Slice Top 15) ---
    const processedData = useMemo(() => {
        if (!data) return [];
        let processed = [...data];

        // 1. Filter
        if (Object.keys(filters).length > 0) {
            processed = processed.filter(row => {
                return Object.entries(filters).every(([key, value]) => {
                    if (!value) return true;
                    const cellVal = row[key] ? String(row[key]).toLowerCase() : "";
                    return cellVal.includes(value.toLowerCase());
                });
            });
        }

        // 2. Sort
        if (sortConfig.key) {
            processed.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                if (typeof bVal === 'string') bVal = bVal.toLowerCase();

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        // 3. Slice Top 15
        return processed.slice(0, 15);
    }, [data, sortConfig, filters]);

    // --- TOTALS ---
    const totals = useMemo(() => {
        if (!processedData.length) return null;
        const sums = {
            sales_2024: 0, sales_2025: 0,
            profit_2024: 0, profit_2025: 0,
            articles_2024: 0, articles_2025: 0,
            share_2024: 0, share_2025: 0,
            profit_share_2024: 0, profit_share_2025: 0
        };
        processedData.forEach(row => {
            sums.sales_2024 += Number(row.sales_2024) || 0;
            sums.sales_2025 += Number(row.sales_2025) || 0;
            sums.profit_2024 += Number(row.profit_2024) || 0;
            sums.profit_2025 += Number(row.profit_2025) || 0;
            sums.articles_2024 += Number(row.articles_2024) || 0;
            sums.articles_2025 += Number(row.articles_2025) || 0;
            sums.share_2024 += Number(row.share_2024) || 0;
            sums.share_2025 += Number(row.share_2025) || 0;
            sums.profit_share_2024 += Number(row.profit_share_2024) || 0;
            sums.profit_share_2025 += Number(row.profit_share_2025) || 0;
        });

        // Re-calc margins and growth
        sums.margin_2024 = sums.sales_2024 ? sums.profit_2024 / sums.sales_2024 : 0;
        sums.margin_2025 = sums.sales_2025 ? sums.profit_2025 / sums.sales_2025 : 0;

        sums.sales_growth = sums.sales_2024 ? (sums.sales_2025 - sums.sales_2024) / sums.sales_2024 : 0;
        sums.profit_growth = sums.profit_2024 ? (sums.profit_2025 - sums.profit_2024) / sums.profit_2024 : 0;
        sums.article_growth = sums.articles_2024 ? (sums.articles_2025 - sums.articles_2024) / sums.articles_2024 : 0;

        return sums;
    }, [processedData]);

    const formatMoney = (val) => val ? Number(val).toLocaleString('ro-RO', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '0';
    const formatInt = (val) => val ? Number(val).toLocaleString('ro-RO') : '0';
    const formatPct = (val) => val ? `${(val * 100).toFixed(0)}%` : '0%';

    const renderHeader = (label, sortKey, filterKey, width) => (
        <TableHead className={`text-black border border-black/20 text-center ${width || ''}`}>
            <div
                className="cursor-pointer flex items-center justify-center gap-1 hover:underline"
                onClick={() => handleSort(sortKey)}
            >
                {label}
                {sortConfig.key === sortKey && (
                    <span className="text-[10px]">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
                )}
            </div>
            <div className="mt-1 px-1">
                <Input
                    className="h-6 w-full text-xs bg-white/50 border-black/10 focus:bg-white text-black px-1"
                    placeholder="..."
                    value={filters[filterKey] || ''}
                    onChange={(e) => handleFilterChange(filterKey, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                />
            </div>
        </TableHead>
    );

    return (
        <Card className="bg-[#18181b] border-white/10 mt-8 mb-40">
            <div className="p-4 border-b border-white/10 flex justify-between items-center bg-yellow-400 rounded-t-lg">
                <div className="flex items-center gap-4">
                    <h3 className="text-lg font-bold text-black">{title}</h3>
                    <span className="text-sm font-bold text-black">Top 15 (din {data ? data.length : 0})</span>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    className="bg-black text-yellow-400 border-none hover:bg-black/80 h-8 font-bold"
                    onClick={handleExport}
                >
                    <Download className="w-4 h-4 mr-2" />
                    Export XLSX
                </Button>
            </div>

            <div className="overflow-x-auto">
                <Table className="border-collapse w-full text-xs">
                    <TableHeader>
                        <TableRow className="border-none bg-yellow-400 text-black font-extrabold hover:bg-yellow-400">
                            <TableHead className="text-black border border-black/20 text-center w-10">Nr</TableHead>
                            {renderHeader("Client", "client", "client", "text-left pl-2 min-w-[150px]")}
                            {renderHeader(`CA ${p1Label}`, "sales_2024", "sales_2024")}
                            {renderHeader(`CA ${p2Label}`, "sales_2025", "sales_2025")}

                            {renderHeader(`Profit ${p1Label}`, "profit_2024", "profit_2024")}
                            {renderHeader(`Profit ${p2Label}`, "profit_2025", "profit_2025")}

                            {renderHeader(`% Profit ${p1Label}`, "margin_2024", "margin_2024")}
                            {renderHeader(`% Profit ${p2Label}`, "margin_2025", "margin_2025")}

                            {renderHeader(`Pondere CA ${p1Label}`, "share_2024", "share_2024")}
                            {renderHeader(`Pondere CA ${p2Label}`, "share_2025", "share_2025")}

                            {renderHeader(`Pondere Profit ${p1Label}`, "profit_share_2024", "profit_share_2024")}
                            {renderHeader(`Pondere Profit ${p2Label}`, "profit_share_2025", "profit_share_2025")}

                            {renderHeader(`Articole ${p1Label}`, "articles_2024", "articles_2024")}
                            {renderHeader(`Articole ${p2Label}`, "articles_2025", "articles_2025")}

                            {renderHeader(`Crestere CA %`, "sales_growth", "sales_growth")}
                            {renderHeader(`Crestere P %`, "profit_growth", "profit_growth")}
                            {renderHeader(`Crestere Art %`, "article_growth", "article_growth")}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {processedData.map((row, i) => (
                            <TableRow key={i} className="hover:bg-white/5 border-b border-white/5 text-gray-300">
                                <TableCell className="text-center border-r border-white/10 font-mono">{i + 1}</TableCell>
                                <TableCell className="font-bold text-white border-r border-white/10">{row.client}</TableCell>

                                <TableCell className="text-right border-r border-white/10">{formatMoney(row.sales_2024)}</TableCell>
                                <TableCell className="text-right border-r border-white/10 font-bold text-white">{formatMoney(row.sales_2025)}</TableCell>

                                <TableCell className="text-right border-r border-white/10">{formatMoney(row.profit_2024)}</TableCell>
                                <TableCell className="text-right border-r border-white/10 font-bold text-white">{formatMoney(row.profit_2025)}</TableCell>

                                <TableCell className="text-center border-r border-white/10">{formatPct(row.margin_2024)}</TableCell>
                                <TableCell className="text-center border-r border-white/10">{formatPct(row.margin_2025)}</TableCell>

                                <TableCell className="text-center border-r border-white/10">{formatPct(row.share_2024)}</TableCell>
                                <TableCell className="text-center border-r border-white/10">{formatPct(row.share_2025)}</TableCell>

                                <TableCell className="text-center border-r border-white/10">{formatPct(row.profit_share_2024)}</TableCell>
                                <TableCell className="text-center border-r border-white/10">{formatPct(row.profit_share_2025)}</TableCell>

                                <TableCell className="text-center border-r border-white/10">{formatInt(row.articles_2024)}</TableCell>
                                <TableCell className="text-center border-r border-white/10 font-bold text-white">{formatInt(row.articles_2025)}</TableCell>

                                <TableCell className={`text-center border-r border-white/10 font-bold ${row.sales_growth >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {formatPct(row.sales_growth)}
                                </TableCell>
                                <TableCell className={`text-center border-r border-white/10 font-bold ${row.profit_growth >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {formatPct(row.profit_growth)}
                                </TableCell>
                                <TableCell className={`text-center border-r border-white/10 font-bold ${row.article_growth >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {formatPct(row.article_growth)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    {/* TOTALS FOOTER */}
                    {totals && (
                        <tfoot className="bg-[#27272a] border-t border-white/20 font-bold text-white sticky bottom-0 z-20">
                            <TableRow className="hover:bg-[#27272a]">
                                <TableCell className="text-center border-r border-white/10 font-mono"></TableCell>
                                <TableCell className="font-bold text-yellow-400 border-r border-white/10 uppercase pl-2">Total</TableCell>
                                <TableCell className="text-right border-r border-white/10">{formatMoney(totals.sales_2024)}</TableCell>
                                <TableCell className="text-right border-r border-white/10 text-yellow-400">{formatMoney(totals.sales_2025)}</TableCell>
                                <TableCell className="text-right border-r border-white/10">{formatMoney(totals.sales_2024)}</TableCell>
                                <TableCell className="text-right border-r border-white/10 text-yellow-400">{formatMoney(totals.profit_2025)}</TableCell>
                                <TableCell className="text-center border-r border-white/10">{formatPct(totals.margin_2024)}</TableCell>
                                <TableCell className="text-center border-r border-white/10">{formatPct(totals.margin_2025)}</TableCell>
                                <TableCell className="text-center border-r border-white/10">{formatPct(totals.share_2024)}</TableCell>
                                <TableCell className="text-center border-r border-white/10">{formatPct(totals.share_2025)}</TableCell>
                                <TableCell className="text-center border-r border-white/10">{formatPct(totals.profit_share_2024)}</TableCell>
                                <TableCell className="text-center border-r border-white/10">{formatPct(totals.profit_share_2025)}</TableCell>
                                <TableCell className="text-center border-r border-white/10">{formatInt(totals.articles_2024)}</TableCell>
                                <TableCell className="text-center border-r border-white/10 text-yellow-400">{formatInt(totals.articles_2025)}</TableCell>
                                <TableCell className={`text-center border-r border-white/10 ${totals.sales_growth >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatPct(totals.sales_growth)}</TableCell>
                                <TableCell className={`text-center border-r border-white/10 ${totals.profit_growth >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatPct(totals.profit_growth)}</TableCell>
                                <TableCell className={`text-center border-r border-white/10 ${totals.article_growth >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatPct(totals.article_growth)}</TableCell>
                            </TableRow>
                        </tfoot>
                    )}

                </Table>
            </div>
        </Card >
    );
}
